import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TableViewIcon from '@mui/icons-material/TableView';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import enGB from 'date-fns/locale/en-GB';
import { DataGrid } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery, reportDownload } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getAccessLogs, downloadReport } from 'src/api/admin/reports';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { startOfDay, endOfDay } from 'date-fns';
interface SearchParams {
	page?: number;
	pageSize?: string;
	dateFrom?: string;
	dateTo?: string;
}
export const Route = createFileRoute('/_admin/reports/access-logs')({
	component: AccessLogsPage,
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		page: search.page as number | undefined,
		pageSize: search.pageSize as string | undefined,
		dateFrom: search.dateFrom as string | undefined,
		dateTo: search.dateTo as string | undefined,
	}),
});

function AccessLogsPage() {
	const { user: authenticatedUser } = useAuthenticatedUser();
	const navigate = useNavigate();
	const search = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();
	const [menuAnchor, setMenuAnchor] = useState<(EventTarget & HTMLButtonElement) | null>(null);

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasPermission = verifyPermission([PERMISSIONS.adminReportsView]);

	useBreadcrumbLevel(0, 'Reports');
	useBreadcrumbLevel(1, 'Access Logs');

	// Parse date values from URL or use today's date
	const dateFromParam = search.dateFrom;
	const dateToParam = search.dateTo;

	const dateFrom = useMemo(() => (dateFromParam ? new Date(dateFromParam) : new Date()), [dateFromParam]);
	const dateTo = useMemo(() => (dateToParam ? new Date(dateToParam) : new Date()), [dateToParam]);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['reports-access-logs', dateFromParam, dateToParam, search.page],
		queryFn: () =>
			getAccessLogs({
				dateFrom: startOfDay(dateFrom).toISOString(),
				dateTo: endOfDay(dateTo).toISOString(),
				page: search.page,
			}),
		enabled: hasPermission,
	});

	const downloadCSVReport = useCallback(async () => {
		const query = constructQuery({
			dateFrom: startOfDay(dateFrom).toISOString(),
			dateTo: endOfDay(dateTo).toISOString(),
			pipe: 'true',
		});
		const blob = await downloadReport('access-logs', query);
		if (blob) {
			reportDownload(blob);
		}
	}, [dateFrom, dateTo]);

	const mappedColumns = useMemo(() => {
		if (!data?.columns) return [];
		return data.columns.map((c) => ({
			field: c.id,
			headerName: c.label,
			flex: 1,
			sortable: false,
		}));
	}, [data?.columns]);

	// Redirect if no permissions
	useEffect(() => {
		if (!hasPermission) {
			navigate({
				to: '/403',
				search: { permissions: 'adminReportsView', occurredAt: '/reports/access-logs', method: 'GET', uuid: undefined },
			});
		}
	}, [hasPermission, navigate]);

	if (!hasPermission) {
		return null;
	}

	const revalidateAccessLogs = () => {
		queryClient.invalidateQueries({ queryKey: ['reports-access-logs'] });
		refetch();
	};

	const handleDateFromChange = (date: Date | null) => {
		if (date) {
			updateQueryParams({ dateFrom: startOfDay(date).toISOString(), page: 0 });
		}
	};

	const handleDateToChange = (date: Date | null) => {
		if (date) {
			updateQueryParams({ dateTo: endOfDay(date).toISOString(), page: 0 });
		}
	};

	const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
		updateQueryParams({ page: model.page, pageSize: model.pageSize });
	};

	return (
		<>
			<div className="px-4 pt-4 flex flex-row flex-wrap gap-2 w-full justify-between">
				<div className="flex flex-row gap-2">
					<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
						<DatePicker
							value={dateFrom}
							onChange={handleDateFromChange}
							slotProps={{
								textField: {
									size: 'small',
								},
							}}
						/>
						<DatePicker
							value={dateTo}
							onChange={handleDateToChange}
							slotProps={{
								textField: {
									size: 'small',
								},
							}}
						/>
					</LocalizationProvider>
				</div>
				<Button onClick={(e) => setMenuAnchor(e.currentTarget)}>
					<MoreVertIcon />
				</Button>

				<Menu
					anchorEl={menuAnchor}
					open={!!menuAnchor}
					onClose={() => setMenuAnchor(null)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					elevation={1}
				>
					<MenuItem
						key={'Export to Excel'}
						onClick={() => {
							downloadCSVReport();
							setMenuAnchor(null);
						}}
					>
						<ListItemIcon>
							<TableViewIcon />
						</ListItemIcon>
						<ListItemText>Export to Excel</ListItemText>
					</MenuItem>
				</Menu>
			</div>
			<div className="p-4 pt-0">
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						data={data?.data || []}
						columns={mappedColumns}
						rowCount={data?.count || 0}
						pageSize={data?.pageSize || 50}
						page={data?.page || 0}
						onPaginationModelChange={handlePaginationModelChange}
						isLoading={isLoading}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						pagination
						onReload={revalidateAccessLogs}
						getRowClassName={() => '!cursor-default'}
					/>
				</Paper>
			</div>
		</>
	);
}
